/**
 * Search and filter utilities for chat conversations
 */

/**
 * Filter conversations by tab (all, favorites, archived)
 */
export function filterConversationsByTab(conversations, tab) {
  switch (tab) {
    case 'favorites':
      return conversations.filter(c => c.favorite === true && c.archived !== true);
    case 'archived':
      return conversations.filter(c => c.archived === true);
    case 'all':
    default:
      return conversations.filter(c => c.archived !== true);
  }
}

/**
 * Search conversations by content or title
 */
export function searchConversations(conversations, searchQuery) {
  if (!searchQuery.trim()) return conversations;
  
  const query = searchQuery.toLowerCase();
  return conversations.filter(conv => {
    // Search in title
    if (conv.title?.toLowerCase().includes(query)) return true;
    
    // Search in messages
    if (conv.messages && Array.isArray(conv.messages)) {
      return conv.messages.some(msg => 
        msg.content?.toLowerCase().includes(query)
      );
    }
    
    return false;
  });
}

/**
 * Filter conversations by experience type
 */
export function filterConversationsByExperienceType(conversations, experienceType, experienceTypes) {
  if (experienceType === 'all') return conversations;
  
  return conversations.filter(conv => {
    if (!conv.projectId) return experienceType === 'none';
    
    // We'd need to fetch the experience to check its type
    // For now, return all conversations with experiences if experienceType is not 'none'
    return experienceType !== 'none';
  });
}

// Legacy export for backward compatibility
export const filterConversationsByProjectType = filterConversationsByExperienceType;

/**
 * Filter conversations by date range
 */
export function filterConversationsByDateRange(conversations, dateRange) {
  if (!dateRange || dateRange === 'all') return conversations;
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  return conversations.filter(conv => {
    if (!conv.updatedAt) return false;
    
    const updatedDate = conv.updatedAt?.toDate ? conv.updatedAt.toDate() : new Date(conv.updatedAt);
    updatedDate.setHours(0, 0, 0, 0);
    
    switch (dateRange) {
      case 'today':
        return updatedDate.getTime() === today.getTime();
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return updatedDate >= weekAgo;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return updatedDate >= monthAgo;
      default:
        return true;
    }
  });
}

/**
 * Sort conversations by most recent
 */
export function sortConversationsByDate(conversations) {
  return [...conversations].sort((a, b) => {
    const dateA = a.updatedAt?.toDate ? a.updatedAt.toDate() : new Date(a.updatedAt || 0);
    const dateB = b.updatedAt?.toDate ? b.updatedAt.toDate() : new Date(b.updatedAt || 0);
    return dateB - dateA; // Most recent first
  });
}

